import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { keyBy } from 'lodash-es'
import { useIsFetching, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useResultsStore } from '@editorStores'
import { getAnchorInterventionTableData, getProjectConnectionPrioritization } from '@queries'
import { saveManualLoadsPerSupport } from '@mutations'
import { getGetDesignForcesPerSupportQueryKey } from 'src/state/api/generated/design-forces-per-support/design-forces-per-support'
import useResultsQueries from '../useResultsQueries'
import { AnchorEdits, buildManualLoadsPayload, editsChangeCheckInputs } from './utils'

export type { AnchorEdit, AnchorEdits } from './utils'

interface UseSaveAnchorManualLoadsResult {
  /** Persists the given edits and recomputes the anchor checks. */
  saveAnchorEdits: (edits: AnchorEdits) => void
  /**
   * True until the saved rows are back in the store — the next payload is built from them, so a
   * second save must not start before that (it would replay the pre-save manual loads).
   */
  isSaving: boolean
  isComputingAnchorChecks: boolean
}

/**
 * Persists manual loads, comments and the Zuganker selection of the "Anker" tab. The endpoint
 * replaces every support at once, so the payload replays all saved rows and only swaps in the
 * edited ones — read from the store at fire time (a render closure could lag behind a refetch and
 * resurrect outdated values).
 *
 * Only a changed manual load or Zuganker recomputes the anchor checks (the backend leaves them
 * valid when merely a comment was written). A comment-only save also skips invalidating the anchor rows and
 * mirrors the saved comments into the store instead: refetching them flips `isLoadingResults`,
 * which replaces the open detail pane with the loading state and drops what it holds.
 */
const useSaveAnchorManualLoads = (): UseSaveAnchorManualLoadsResult => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { computeAnchorChecks, isComputingAnchorChecks } = useResultsQueries(projectId as string)

  const setAnchorInterventionTableData = useResultsStore(
    state => state.setAnchorInterventionTableData,
  )

  const isReloadingAnchorRows =
    useIsFetching({ queryKey: getAnchorInterventionTableData.getKey(projectId as string) }) > 0

  const { mutate, isLoading: isSavingRequest } = useMutation(
    async (edits: AnchorEdits) => {
      const anchorRows = useResultsStore.getState().anchorInterventionTableData
      if (!projectId || !anchorRows) return

      const requestData = buildManualLoadsPayload(anchorRows, edits)
      const checkInputsChanged = editsChangeCheckInputs(anchorRows, edits)

      await saveManualLoadsPerSupport.request(projectId, requestData)

      return { requestData, checkInputsChanged }
    },
    {
      onSuccess: async result => {
        if (!result) return

        if (result.checkInputsChanged) {
          // Also invalidates the anchor rows and the checks.
          computeAnchorChecks(projectId as string)
          await queryClient.invalidateQueries(
            getProjectConnectionPrioritization.getKey(projectId as string),
          )
          await queryClient.invalidateQueries(
            getGetDesignForcesPerSupportQueryKey(projectId as string),
          )
        } else {
          const anchorRows = useResultsStore.getState().anchorInterventionTableData
          if (anchorRows) {
            const savedBySupport = keyBy(result.requestData, 'support_id')
            setAnchorInterventionTableData(
              anchorRows.map(row => ({
                ...row,
                comment: savedBySupport[row.support_guid]?.comment ?? row.comment,
              })),
            )
          }
        }

        enqueueSnackbar('Erfolgreich gespeichert', {
          variant: 'success',
          preventDuplicate: true,
          key: 'anchor-manual-loads-saved',
        })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern'), { variant: 'error' })
      },
    },
  )

  const saveAnchorEdits = useCallback((edits: AnchorEdits) => mutate(edits), [mutate])

  return {
    saveAnchorEdits,
    isSaving: isSavingRequest || isReloadingAnchorRows,
    isComputingAnchorChecks,
  }
}

export default useSaveAnchorManualLoads
